import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Icon } from '../elements/Icon';
import { getRecordFileIcon, RecordClass } from '../utils/recordFileIcon';

// ─── Color tokens ────────────────────────────────────────────────────────────

const HEX_LOCHMARA     = '#008dd0';
const HEX_GRAY_150     = '#5a5a5a';
const HEX_MINE_SHAFT   = '#444444';
const HEX_BADGE_BG     = '#f6fbfe';

type AnyProps = { theme?: { colors?: Record<string, string> } };
const t = (token: string, fallback: string) => (props: AnyProps): string =>
  props.theme?.colors?.[token] ?? fallback;

// ─── Types ───────────────────────────────────────────────────────────────────

export type RecordFileBadgeProps = {
  /** Name of the record file (e.g., "patient_record.pdf") */
  recordFileName: string;
  /** Record class (Pathology, Radiology, Clinical, Other) — used to resolve the icon */
  recordClass?: RecordClass;
  /** Explicit file extension (e.g., "pdf", "dcm") — takes priority over extracting from fileName */
  fileExtension?: string;
  className?: string;
};

// ─── Styled components ───────────────────────────────────────────────────────

const BadgeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0px 6px;
  background-color: ${t('recordCardBg', HEX_BADGE_BG)};
  border-radius: 4px;
  align-self: flex-start;
  max-width: 100%;
  min-width: 0;
`;

const BadgeLabel = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-size: 10px;
  font-weight: 400;
  color: ${HEX_GRAY_150};
  white-space: nowrap;
  flex-shrink: 0;
`;

const BadgeName = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-size: 10px;
  font-weight: 400;
  color: ${HEX_MINE_SHAFT};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export const RecordFileBadge: React.FC<RecordFileBadgeProps> = ({
  recordFileName,
  recordClass,
  fileExtension,
  className,
}) => {
  const iconName = getRecordFileIcon(recordFileName, recordClass, fileExtension);
  const theme = useTheme() as AnyProps['theme'];
  const iconColor = theme?.colors?.main ?? HEX_LOCHMARA;

  return (
    <BadgeWrapper className={className} title={recordFileName}>
      <BadgeLabel>Record file:</BadgeLabel>
      <Icon icon={iconName} size={12} color={iconColor} />
      <BadgeName>{recordFileName}</BadgeName>
    </BadgeWrapper>
  );
};

export default RecordFileBadge;
